import MonitorIcon from '@mui/icons-material/Monitor';
import WordpressIcon from '../../components/SvgIcons/WordpressIcon';

export const genptTags = {
    unauthenticated: {
        title: "Unauth",
        bg_color: "var(--grey2)",
    },
    authenticated: {
        title: "Auth",
        bg_color: "var(--grey1)",
    },
    quick: {
        title: "Express",
        bg_color: "var(--grey1)",
    },
    normal: {
        title: "Site-wide",
        bg_color: "var(--grey1)",
    },
};

// genpt test status enum
export const genptTestStatus = {
    inprogress: {
        title: "In Progress",
        bg_color: "var(--functional-blue-600)",
    },
    completed: {
        title: "Completed",
        bg_color: "var(--secondary-700)",
    },
    failed: {
        title: "Failed",
        bg_color: "var(--functional-red)",
    },
};

export const genptFindingSeverity = {
    critical: {
        title: "Critical",
        color: "white",
        bg_color: "var(--functional-red-900)",
    },
    high: {
        title: "High",
        color: "white",
        bg_color: "var(--functional-red-700)",
    },
    medium: {
        title: "Medium",
        color: "white",
        bg_color: "var(--functional-orange)",
    },
    low: {
        title: "Low",
        color: "black",
        bg_color: "var(--functional-yellow)",
    },
    info: {
        title: "Informational",
        color: "black",
        bg_color: "var(--functional-blue-50)",
    },
};

export const genptFindingSeverityGraphColor = {
    critical: {
        title: "Critical",
        color: "var(--functional-red-900)",
    },
    high: {
        title: "High",
        color: "var(--functional-red-700)",
    },
    medium: {
        title: "Medium",
        color: "var(--functional-orange)",
    },
    low: {
        title: "Low",
        color: "var(--functional-yellow)",
    },
    info: {
        title: "Informational",
        color: "var(--functional-blue-50)",
    },
};


export const gentPTAssetTypeMap = {
    web_app: {
        title: "Web App",
        shortTitle: "Web",
        icon: (<MonitorIcon className={"text-primary-500 align-top"} />)
    },
    wordpress: {
        title: "WordPress",
        icon: <WordpressIcon />
    }
}

export const genptAssetToTemplateCountMap = {
    web_app: {
        unauthenticated: 4928,
    },
    wordpress: {
        unauthenticated: 10442,
        authenticated: 10584,
    }
}

export const findingStatusColors = {
    new: {
        name: "New",
        color: "var(--functional-red-900)",
        description: "Initial status of the newly discovered vulnerability."
    },
    existing: {
        name: "Existing",
        color: "var(--functional-red-700)",
        description: "Known issue under investigation or resolution."
    },
    reopened: {
        name: "Re-opened",
        color: "var(--functional-orange)",
        description: "Previously resolved issue that has reoccurred."
    },
    fixed: {
        name: "Fixed",
        color: "var(--success-green)",
        description: "Issue resolved with no further action required."
    },
}

export const testActivityMaps = {
    unauthenticated: {
        name: "Unauthenticated Testing",
        sub_activities: ["Verifying Core Integrity", "Assessing Plugin Review", "Evaluating Theme Safety"]
    },
    authenticated: {
        name: "Authenticated Testing",
        sub_activities: ["In-depth Core Examination", "Authenticated Plugin Checks", "Thorough Theme Inspection"]
    },
    exposures: {
        name: "Sensitive Data Exposure Checks",
    },
    miscellaneous: {
        name: "General Security Assessments",
    },
    misconfiguration: {
        name: "Configuration Integrity Review",
    },
    'default_logins': {
        name: "Authentication Security Evaluation ",
    },
    cves: {
        name: "Known Vulnerabilities Review",
    },
    'exposed_panels': {
        name: "Access Control Checks",
    },
    vulnerabilities: {
        name: "Targeted Threat Identification ",
    },
    fuzzing: {
        name: "Hidden Directories and Files Discovery",
    },
    crawling: {
        name: "Crawling",
    },
    crawling_additional_checks: {
        name: "Additional Checks",
    },
}

export const GPTTestConfigDefaults = {
    vulnerability: {
        rate_limit: 80,
        concurrency: 10,
        timeout: 30,
        retries: 2,
    },
    validation: {
        timeout: 30,
        retries: 2,
    },
};

// GenPT test-coverage options for API assets (ROAR-4426). `idor`/`comprehensive` initiate the IDOR flow.
export const GENPT_COVERAGE = {
    DAST: 'dast',
    COMPREHENSIVE: 'comprehensive',
    IDOR: 'idor',
};

// Coverage cards rendered on the API "Coverage" stepper step. Copy verbatim from the Figma IDOR-flow.
// Order matches the Figma reference: Comprehensive (recommended) → DAST → IDOR.
export const genptCoverageOptions = [
    {
        value: GENPT_COVERAGE.COMPREHENSIVE,
        title: "Comprehensive Security Coverage",
        description: "Covers all discovered endpoints for common vulnerabilities while also performing deeper authorization checks, including IDOR, on high-risk areas.",
        recommended: true,
        runsIdor: true,
    },
    {
        value: GENPT_COVERAGE.DAST,
        title: "DAST Analysis",
        description: "Tests endpoints to uncover general vulnerabilities like server misconfigurations and authentication flaws. Recommended for establishing a basic security posture.",
        recommended: false,
        runsIdor: false,
    },
    {
        value: GENPT_COVERAGE.IDOR,
        title: "IDOR Analysis",
        description: "Targets endpoints susceptible to IDOR to uncover unauthorized data exposure. Ideal for deeply validating user data isolation.",
        recommended: false,
        runsIdor: true,
    },
];

// True when a coverage selection should trigger the IDOR pre-declaration flow.
// Derived from the `runsIdor` flag on genptCoverageOptions so the cards stay the single source of truth.
export const coverageRunsIdor = (coverage) =>
    !!genptCoverageOptions.find((o) => o.value === coverage)?.runsIdor;

// Infer the coverage a prior session used from its persisted `test_type`. The backend does NOT store
// coverage separately (only `test_type`: 'idor' | 'comprehensive' | 'normal'), so "Run last setup"
// reconstructs it here. Note: a Comprehensive run that found no IDOR candidates degrades to 'normal'
// server-side and is therefore indistinguishable from a plain DAST run — it replays as DAST.
export const coverageFromTestType = (testType) => {
    switch (testType) {
        case 'idor':
            return GENPT_COVERAGE.IDOR;
        case 'comprehensive':
            return GENPT_COVERAGE.COMPREHENSIVE;
        default:
            return GENPT_COVERAGE.DAST;
    }
};

// OWASP API Security Top 10 — radar axes shown alongside the coverage cards. Each axis carries its
// per-coverage `scores` (relative depth 0–100, keyed by GENPT_COVERAGE value), the single source of
// truth that drives the radar shape. IDOR concentrates on object-level auth (API-1/API-3); DAST
// spreads across misconfiguration/SSRF/auth; comprehensive is the union (deepest overall).
export const owaspApiTop10 = [
    { key: "API-1", label: "Broken Object Level Auth.", scores: { dast: 25, comprehensive: 95, idor: 100 } },
    { key: "API-2", label: "Broken Authentication", scores: { dast: 80, comprehensive: 85, idor: 30 } },
    { key: "API-3", label: "Broken Object Property Level Auth.", scores: { dast: 25, comprehensive: 90, idor: 95 } },
    { key: "API-4", label: "Unrestricted Resource Consumption", scores: { dast: 70, comprehensive: 75, idor: 20 } },
    { key: "API-5", label: "Broken Function Level Auth.", scores: { dast: 60, comprehensive: 70, idor: 20 } },
    { key: "API-6", label: "Unrestricted Access to Sensitive Business Flows", scores: { dast: 25, comprehensive: 25, idor: 30 } },
    { key: "API-7", label: "Server Side Request Forgery", scores: { dast: 85, comprehensive: 85, idor: 15 } },
    { key: "API-8", label: "Security Misconfiguration", scores: { dast: 90, comprehensive: 90, idor: 20 } },
    { key: "API-9", label: "Improper Inventory Management", scores: { dast: 70, comprehensive: 75, idor: 25 } },
    { key: "API-10", label: "Unsafe Consumption of APIs", scores: { dast: 65, comprehensive: 70, idor: 15 } },
];

// Derived coverage → { OWASP key: score } lookup for by-coverage access (radar values, tests).
export const owaspCoverageScores = Object.fromEntries(
    Object.values(GENPT_COVERAGE).map((c) => [
        c, Object.fromEntries(owaspApiTop10.map((a) => [a.key, a.scores[c] ?? 0])),
    ])
);


// Per-test IDOR result → colour token (mirrors genptTestStatus styling).
export const idorTestResultMap = {
    pending: { title: "Pending", bg_color: "var(--functional-blue-600)" },
    secure: { title: "Secure", bg_color: "var(--secondary-700)" },
    vulnerable: { title: "Vulnerable", bg_color: "var(--functional-red)" },
    skipped: { title: "Skipped", bg_color: "var(--neutral-400)" },
};

